import React, { useState } from 'react';
import { CITY_ROUTES } from '../cityRoutes';
import { useWorldStore } from '../state/worldStore';

// RoutesPanel — on-screen controls for the ?routes=edit lane / car-depth editor.
// Selection (route + node) lives in worldStore.routeEdit, shared with the
// clickable 3D node handles and the keyboard — so click a node in the scene OR
// use these buttons; they stay in sync. Add/delete nodes let a route snake
// through the city; node MOVES dispatch the same arrow keys the editor listens
// for (no editor refactor). Copy builds the paste-ready line from saved edits.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const key = (k, shift = false) => window.dispatchEvent(new KeyboardEvent('keydown', { key: k, shiftKey: shift, bubbles: true }));
const savedPts = (route) => { try { const s = JSON.parse(localStorage.getItem(`route:${route.id}`) || 'null'); return (s?.length) ? s : route.points; } catch { return route.points; } };

const panel = {
  position: 'fixed', left: safe('left', 16), top: `calc(${safe('top', 16)} + 60px)`, zIndex: 40,
  width: 214, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const arrow = { ...btn, padding: '9px 0' };

export default function RoutesPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'routes';
  const routeIdx = useWorldStore((s) => s.routeEdit.routeIdx);
  const ptIdx = useWorldStore((s) => s.routeEdit.ptIdx);
  const setRouteEdit = useWorldStore((s) => s.setRouteEdit);
  const [fine, setFine] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!ON) return null;

  const route = CITY_ROUTES[routeIdx] || CITY_ROUTES[0];
  const ptCount = savedPts(route).length;
  const cyclePt = (d) => setRouteEdit({ ptIdx: (ptIdx + d + ptCount) % ptCount });

  const copyLine = () => {
    const line = `{ id: '${route.id}', color: '${route.color}', points: ${JSON.stringify(savedPts(route))}, cars: ${route.cars}, speed: ${route.speed} },`;
    navigator.clipboard?.writeText(line).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }).catch(() => {});
  };

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>Routes · lanes / cars</div>

      {/* Route (lane) picker */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 10 }}>
        {CITY_ROUTES.map((r, i) => (
          <button key={r.id} onClick={() => setRouteEdit({ routeIdx: i, ptIdx: 0 })}
            style={{ ...btn, fontSize: 11.5, padding: '6px 0',
              background: i === routeIdx ? '#ffb25e' : 'rgba(255,178,94,0.14)',
              color: i === routeIdx ? '#1c1206' : '#ffd27d', borderColor: r.color }}>
            {r.id}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ opacity: 0.85 }}>node {Math.min(ptIdx + 1, ptCount)}/{ptCount}</span>
        <span style={{ opacity: 0.6, fontSize: 11 }}>{route.cars} cars · spd {route.speed}</span>
      </div>
      <div style={{ opacity: 0.6, fontSize: 10.5, marginBottom: 8 }}>Tip: click a node in the scene to select it.</div>

      <div style={{ display: 'flex', gap: 5, marginBottom: 6 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => cyclePt(-1)}>◀ node</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => cyclePt(1)}>node ▶</button>
      </div>
      <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
        <button style={{ ...btn, flex: 1, background: 'rgba(157,255,125,0.16)', borderColor: 'rgba(157,255,125,0.5)', color: '#c8ffb0' }} onClick={() => key('n')}>＋ add node</button>
        <button style={{ ...btn, flex: 1, background: 'rgba(255,94,120,0.14)', borderColor: 'rgba(255,94,120,0.5)', color: '#ffb0bd' }} onClick={() => key('x')}>－ delete</button>
      </div>

      {/* Nudge pad — Up/Down = depth (z), Left/Right = across (x) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 4 }}>
        <span />
        <button style={arrow} onClick={() => key('ArrowUp', fine)}>↑</button>
        <span />
        <button style={arrow} onClick={() => key('ArrowLeft', fine)}>←</button>
        <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>depth</div>
        <button style={arrow} onClick={() => key('ArrowRight', fine)}>→</button>
        <span />
        <button style={arrow} onClick={() => key('ArrowDown', fine)}>↓</button>
        <span />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '8px 0 10px', cursor: 'pointer', opacity: 0.9 }}>
        <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
        fine steps (0.5)
      </label>

      <button style={{ ...btn, width: '100%', background: copied ? '#9dff7d' : 'rgba(255,178,94,0.14)', color: copied ? '#0c2c06' : '#ffd27d' }} onClick={copyLine}>
        {copied ? '✓ copied' : `copy ${route.id} line`}
      </button>
      <div style={{ opacity: 0.55, fontSize: 10.5, marginTop: 8, lineHeight: 1.35 }}>
        Paste into <code>src/cityRoutes.js</code>. Up/Down = depth into the scene.
      </div>
    </div>
  );
}
